import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  FirestoreDataConverter
} from "firebase/firestore";
import { firestore } from "@/base";
import { getCurrentUser } from "@/services/auth";
import type { TTemplateRelatorio, TTemplateRelatorioInput } from "../types";

/**
 * Converter para documentos de template de relatório
 */
const templateConverter: FirestoreDataConverter<TTemplateRelatorio> = {
  toFirestore(data) {
    const dataToSave = { ...data };
    delete (dataToSave as Partial<TTemplateRelatorio>).id;
    return dataToSave;
  },
  fromFirestore(snap) {
    const data = snap.data();
    return {
      ...data,
      id: snap.id,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt
    } as TTemplateRelatorio;
  }
};

/**
 * Get templates collection reference (subcollection inside licenciada)
 * @param licenciadaId
 */
function getTemplatesCollectionReference(licenciadaId: string) {
  return collection(firestore, "licenciadas", licenciadaId, "templates_relatorio").withConverter(templateConverter);
}

/**
 * Get template document reference
 * @param licenciadaId
 * @param templateId
 */
function getTemplateDocumentReference(licenciadaId: string, templateId: string) {
  const collectionReference = getTemplatesCollectionReference(licenciadaId);
  return doc(collectionReference, templateId);
}

/**
 * Get all templates for a licenciada
 * @param licenciadaId
 */
export async function getTemplates(licenciadaId: string): Promise<TTemplateRelatorio[]> {
  const collectionReference = getTemplatesCollectionReference(licenciadaId);
  const q = query(collectionReference, where("ativo", "==", true), orderBy("nome", "asc"));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => document_.data());
}

/**
 * Get default templates (isPadrao = true)
 * @param licenciadaId
 */
export async function getDefaultTemplates(licenciadaId: string): Promise<TTemplateRelatorio[]> {
  const collectionReference = getTemplatesCollectionReference(licenciadaId);
  const q = query(
    collectionReference,
    where("ativo", "==", true),
    where("isPadrao", "==", true),
    orderBy("nome", "asc")
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((document_) => document_.data());
}

/**
 * Get a template by ID
 * @param licenciadaId
 * @param templateId
 */
export async function getTemplateById(licenciadaId: string, templateId: string): Promise<TTemplateRelatorio | null> {
  const documentReference = getTemplateDocumentReference(licenciadaId, templateId);
  const snapshot = await getDoc(documentReference);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
}

/**
 * Create a new template
 * @param licenciadaId - ID da licenciada dona do template
 * @param data - Template data
 */
export async function createTemplate(
  licenciadaId: string,
  data: Omit<TTemplateRelatorioInput, "createdBy" | "updatedBy" | "createdAt" | "updatedAt" | "idLicenciada">
): Promise<TTemplateRelatorio> {
  const currentUserId = getCurrentUser()?.uid;
  if (!currentUserId) {
    throw new Error("User not authenticated");
  }

  if (!licenciadaId) {
    throw new Error("idLicenciada is required");
  }

  const collectionReference = getTemplatesCollectionReference(licenciadaId);

  const templateData = {
    ...data,
    idLicenciada: licenciadaId,
    versao: 1,
    ativo: true,
    createdBy: currentUserId,
    updatedBy: currentUserId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const documentReference = await addDoc(collectionReference, templateData as unknown as TTemplateRelatorio);
  const newDocument = await getDoc(documentReference);

  return newDocument.data()!;
}

/**
 * Update a template
 * @param licenciadaId
 * @param templateId
 * @param data
 */
export async function updateTemplate(
  licenciadaId: string,
  templateId: string,
  data: Partial<Omit<TTemplateRelatorio, "id" | "createdAt" | "createdBy" | "idLicenciada">>
): Promise<void> {
  const currentUserId = getCurrentUser()?.uid;
  if (!currentUserId) {
    throw new Error("User not authenticated");
  }

  const documentReference = getTemplateDocumentReference(licenciadaId, templateId);

  // Get current version for increment
  const currentDocument = await getDoc(documentReference);
  if (!currentDocument.exists()) {
    throw new Error("Template not found");
  }

  const currentData = currentDocument.data();
  const newVersion = (currentData.versao || 0) + 1;

  await updateDoc(documentReference, {
    ...data,
    versao: newVersion,
    updatedBy: currentUserId,
    updatedAt: serverTimestamp()
  });
}

/**
 * Soft delete a template (set ativo = false)
 * @param licenciadaId
 * @param templateId
 */
export async function deleteTemplate(licenciadaId: string, templateId: string): Promise<void> {
  const currentUserId = getCurrentUser()?.uid;
  if (!currentUserId) {
    throw new Error("User not authenticated");
  }

  const documentReference = getTemplateDocumentReference(licenciadaId, templateId);

  await updateDoc(documentReference, {
    ativo: false,
    updatedBy: currentUserId,
    updatedAt: serverTimestamp()
  });
}
